// Phase-3 Slice 27 + 28 — Rename preview/apply dialog.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 27 + § Slice 28.
//
// Reads from `useRenamePreviewStore` (the same Zustand bridge a future
// "Rename column…" context-menu handler will populate). Renders:
//   - The schema-side ALTER for the rename (always shown).
//   - One row per rewritten object body (collapsed; click to see old/new).
//   - A "Cannot rewrite" section listing parse-failed / body-missing /
//     no-emitter / unsupported-op objects (the user can choose to skip them or abort).
//   - An Apply button. Slice 28 wires the executor; per-ALTER outcome is
//     surfaced after apply (success / failure / refused-stub).
//
// No diff library used (the npm `diff` package isn't a project dep) — the
// old/new bodies are shown side-by-side as plain monospace blocks.

import { useEffect, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import type {
  AlterStatementResult,
  ObjectBodyEdit,
  RenameResult,
  UnrewritableObject,
} from '../types';

import { databaseComparisonKey } from '../connection-lifecycle/databaseIdentity';

import { canApplyRename } from './canApplyRename';
import type { PlanRenameResult } from './renamePlanner';
import { useRenamePreviewStore, type RenamePreviewInput } from './renamePreviewStore';

export function RenamePreviewDialog() {
  const {
    status,
    input,
    result,
    applyResult,
    errorMessage,
    applyBatch,
    close,
    nameInputIntent,
    submitNewName,
  } = useRenamePreviewStore();
  const open = status !== 'closed';
  const canApply = canApplyRename(status, result);
  const isApplying = status === 'applying';
  const showApplyResult = (status === 'applied' || status === 'apply-error') && applyResult;
  const isNameInput = status === 'name-input' && nameInputIntent != null;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) close(); }}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Rename preview</DialogTitle>
          <DialogDescription>
            {isNameInput
              ? 'Enter a new name for the selected object. The preview shows the ALTERs before any changes are executed.'
              : 'Review the schema and per-object ALTERs before applying. No changes are executed until you click Apply.'}
          </DialogDescription>
        </DialogHeader>
        {isNameInput && (
          <NameInputBody
            initialName={nameInputIntent.fromIdentifier.name}
            kind={nameInputIntent.kind}
            onSubmit={(newName) => { void submitNewName(newName); }}
            onCancel={close}
          />
        )}
        {status === 'loading' && <p className="text-sm text-muted-foreground">Planning rename…</p>}
        {status === 'error' && (
          <p className="text-sm text-destructive">Failed to plan rename: {errorMessage}</p>
        )}
        {(status === 'ready' || isApplying || showApplyResult) && result && (
          <PreviewBody input={input} result={result} />
        )}
        {showApplyResult && applyResult && <ApplyResultBody result={applyResult} />}
        {!isNameInput && (
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={isApplying}>
              {showApplyResult ? 'Done' : 'Close'}
            </Button>
            {!showApplyResult && (
              <Button
                onClick={() => { void applyBatch(); }}
                disabled={!canApply || isApplying}
              >
                {isApplying ? 'Applying…' : 'Apply'}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function NameInputBody({
  initialName,
  kind,
  onSubmit,
  onCancel,
}: {
  readonly initialName: string;
  readonly kind: 'column' | 'table' | 'view';
  readonly onSubmit: (newName: string) => void;
  readonly onCancel: () => void;
}) {
  const [value, setValue] = useState(initialName);
  useEffect(() => {
    setValue(initialName);
  }, [initialName]);
  const trimmed = value.trim();
  const canSubmit = trimmed.length > 0 && trimmed !== initialName;
  return (
    <form
      data-testid="rename-name-input"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit(value);
      }}
      className="space-y-3"
    >
      <div>
        <label className="text-xs text-muted-foreground" htmlFor="rename-new-name">
          Rename {kind} <span className="font-mono">{initialName}</span> to:
        </label>
        <Input
          id="rename-new-name"
          data-testid="rename-name-input-field"
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={!canSubmit} data-testid="rename-name-input-submit">
          Continue
        </Button>
      </DialogFooter>
    </form>
  );
}

function PreviewBody({
  input,
  result,
}: {
  readonly input: ReturnType<typeof useRenamePreviewStore.getState>['input'];
  readonly result: PlanRenameResult;
}) {
  const { setIncludeAmbiguous, status } = useRenamePreviewStore();
  const { track } = useAnalytics();
  if (result.status !== 'ok') {
    return (
      <p data-testid="rename-plan-failure" className="text-sm text-muted-foreground">
        {planFailureMessage(result)}
      </p>
    );
  }
  const batch = result.batch;
  const targetLabel = `${input?.request.fromIdentifier.name ?? '?'} → ${input?.request.toIdentifier.name ?? '?'}`;
  const ambiguousCount = batch.ambiguousMatchCount ?? 0;
  const includeAmbiguous = input?.includeAmbiguous ?? false;
  return (
    <div className="space-y-4">
      <section>
        <h3 className="text-sm font-semibold mb-1">Schema rename ({targetLabel})</h3>
        <TargetDatabaseLine input={input} />
        <pre className="font-mono text-xs whitespace-pre-wrap rounded border bg-muted p-2">
          {batch.schemaAlter.sql}
        </pre>
      </section>
      <section>
        <h3 className="text-sm font-semibold mb-1">
          Object bodies to rewrite ({batch.objectEdits.length})
        </h3>
        {batch.objectEdits.length === 0 ? (
          <p className="text-sm text-muted-foreground">No object bodies reference this identifier.</p>
        ) : (
          <ul className="space-y-2">
            {batch.objectEdits.map((edit, i) => (
              <ObjectBodyRow key={`${edit.objectKind}-${i}`} edit={edit} />
            ))}
          </ul>
        )}
        {ambiguousCount > 0 && (
          <label
            data-testid="rename-include-ambiguous"
            className="mt-2 flex items-start gap-2 text-xs text-muted-foreground cursor-pointer"
          >
            <input
              type="checkbox"
              className="mt-0.5"
              checked={includeAmbiguous}
              disabled={status !== 'ready'}
              onChange={(e) => {
                const enabled = e.target.checked;
                track('feature:rename_include_ambiguous', { enabled });
                void setIncludeAmbiguous(enabled);
              }}
            />
            <span>
              Include {ambiguousCount} ambiguous rewrite{ambiguousCount === 1 ? '' : 's'}.
              These references — a FROM name with no schema, a four-part
              linked-server name, or a column whose table could not be
              resolved — may belong to a same-named object elsewhere; review each
              before enabling.
            </span>
          </label>
        )}
      </section>
      {batch.unrewritable.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold mb-1">
            Cannot rewrite ({batch.unrewritable.length})
          </h3>
          <ul className="text-sm space-y-1">
            {batch.unrewritable.map((u, i) => (
              <UnrewritableRow key={`${u.objectKind}-${i}`} unrewritable={u} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/**
 * Which database this rename actually RUNS in — shown only when that is not the
 * database the invoking tab is bound to.
 *
 * Rename routes at the database the typed name resolved to, not at the tab's:
 * F2 on `B.dbo.users` from an `A` tab plans against `B`'s identifier index,
 * fetches `B`'s bodies and executes `B`'s ALTERs. Nothing in the header said so,
 * and on SQL Server the rename ALTER is `EXEC sp_rename '[dbo].[users]'` —
 * which carries no database of its own, so the routing is the ONLY thing that
 * decides which server-side object Apply renames.
 *
 * Silent in three cases, all of which would be noise rather than a warning:
 * a rename that runs in the tab's own database; an invoking surface that has no
 * tab database to contrast with (the Object Explorer, whose node IS the routed
 * database); and MySQL / Oracle / SQLite, where one connection addresses a
 * single catalog and a "database" is a schema the same session already sees
 * (`connection-lifecycle/databaseIdentity.ts` keys those by connection, not by
 * database).
 */
function TargetDatabaseLine({ input }: { readonly input: RenamePreviewInput | null }) {
  if (!input) return null;
  const { connectionId, database, tabDatabase } = input;
  const engineId = input.request.engineId;
  if (!tabDatabase) return null;
  if (engineId === 'mysql' || engineId === 'oracle' || engineId === 'sqlite') return null;
  // The same comparison the References tab reuses a tab by — never a hand-rolled
  // lower-case compare, which would call a `_CS_` instance's `Sales` and `sales`
  // one database.
  const routedKey = databaseComparisonKey(connectionId, engineId, database);
  const tabKey = databaseComparisonKey(connectionId, engineId, tabDatabase);
  if (routedKey === tabKey) return null;
  return (
    <p data-testid="rename-target-database" className="text-xs text-muted-foreground mb-1">
      Runs in database {database} (this tab is bound to {tabDatabase}).
    </p>
  );
}

function ObjectBodyRow({ edit }: { readonly edit: ObjectBodyEdit }) {
  const fq = qualifiedLabel(edit.objectName);
  const isAmbiguous = edit.confidence === 'ambiguous';
  return (
    <li className="rounded border p-2">
      <div className="text-xs font-mono">
        {edit.objectKind} · <span className="font-semibold">{fq}</span>
        {isAmbiguous && (
          <span
            data-testid="rename-edit-ambiguous-badge"
            className="ml-2 rounded bg-amber-100 px-1 py-0.5 text-[10px] font-sans font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
          >
            ambiguous match
          </span>
        )}
      </div>
      <pre className="font-mono text-xs whitespace-pre-wrap mt-1 bg-muted p-2 rounded">
        {edit.alter.sql}
      </pre>
      <details className="mt-1">
        <summary className="text-xs text-muted-foreground cursor-pointer">
          Diff (original ↔ rewritten body)
        </summary>
        <div className="grid grid-cols-2 gap-2 mt-1 text-xs">
          <pre className="font-mono whitespace-pre-wrap bg-muted p-2 rounded overflow-x-auto">
            {edit.originalBody}
          </pre>
          <pre className="font-mono whitespace-pre-wrap bg-muted p-2 rounded overflow-x-auto">
            {edit.newBody}
          </pre>
        </div>
      </details>
    </li>
  );
}

function UnrewritableRow({ unrewritable }: { readonly unrewritable: UnrewritableObject }) {
  return (
    <li className="font-mono text-xs">
      <span className="font-semibold">{qualifiedLabel(unrewritable.objectName)}</span>
      {' — '}
      {unrewritable.objectKind} <span className="text-muted-foreground">({unrewritable.reason})</span>
    </li>
  );
}

function ApplyResultBody({ result }: { readonly result: RenameResult }) {
  const headline = applyHeadline(result.status);
  const tone = result.status === 'success' ? 'text-green-700' : 'text-destructive';
  return (
    <section className="mt-4 border-t pt-4 space-y-2">
      <h3 className={`text-sm font-semibold ${tone}`}>{headline}</h3>
      {result.errorMessage && (
        <p className="text-xs text-destructive font-mono">{result.errorMessage}</p>
      )}
      <ul className="space-y-1">
        {result.alters.map((a, i) => (
          <AlterResultRow key={`alter-${i}`} entry={a} />
        ))}
      </ul>
      {result.undoExecuted && result.undoExecuted.length > 0 && (
        <details>
          <summary className="text-xs text-muted-foreground cursor-pointer">
            Partial undo ran ({result.undoExecuted.length} statements)
          </summary>
          <ul className="space-y-1 mt-1">
            {result.undoExecuted.map((a, i) => (
              <AlterResultRow key={`undo-${i}`} entry={a} />
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function AlterResultRow({ entry }: { readonly entry: AlterStatementResult }) {
  const sym = entry.status === 'success' ? '✓' : '✗';
  const tone = entry.status === 'success' ? 'text-green-700' : 'text-destructive';
  return (
    <li className="text-xs font-mono">
      <span className={tone}>{sym}</span>
      {' '}
      <span className="text-muted-foreground">{entry.alter.purpose}</span>
      {entry.alter.objectName && (
        <>
          {' '}
          <span className="font-semibold">{qualifiedLabel(entry.alter.objectName)}</span>
        </>
      )}
      {entry.error && (
        <>
          {' — '}
          <span className="text-destructive">{entry.error}</span>
        </>
      )}
    </li>
  );
}

/** Plain sentences for a plan that produced no batch. The raw status token
 *  used to be printed here ("Cannot plan rename: no-index"), which every
 *  disconnect / reconnect during a rename now routes to — the readiness wait
 *  answers `no-index` for a cancelled build too, so the token became ordinary
 *  user-facing copy. */
function planFailureMessage(result: Exclude<PlanRenameResult, { status: 'ok' }>): string {
  switch (result.status) {
    case 'no-index':
      return 'The identifier index for this database is not available yet. Try again.';
    case 'unsupported-engine':
      return 'Rename is not available for this connection.';
    case 'unsupported-kind':
      return `This object cannot be renamed: ${result.reason}`;
    case 'missing-table-context':
      return 'This rename needs the table the column belongs to. Start it again from the column.';
    case 'missing-emitter':
      return 'Renaming tables and views is not available for this connection.';
  }
}

function applyHeadline(status: RenameResult['status']): string {
  switch (status) {
    case 'success':
      return 'Rename applied successfully.';
    case 'rolled-back':
      return 'Rename failed and was rolled back.';
    case 'partial-failure':
      return 'Rename failed; partial undo issued.';
    case 'refused':
      return 'Rename refused.';
  }
}

function qualifiedLabel(qn: { database?: string; schema?: string; name: string }): string {
  return [qn.database, qn.schema, qn.name].filter(Boolean).join('.');
}
